import { computed } from '@ember/object';
import BaseActualFilterAmplitude from '../../implementation-common/base-actual-filter-amplitude';
import FilterConstants from '../../../../filter-constants';
import * as CommonUtils from '../../../../utils/common';
import * as Complex from '../../../process-data/complex';

export default BaseActualFilterAmplitude.extend({
  r1: null,
  r2: null,
  r3: null,
  r4: null,
  c1: null,
  c2: null,
  opAmp: null,
  complexS: computed('sImaginary', function () {
    var sImaginary = this.sImaginary;
    var result = Complex.createComplex(0, sImaginary);
    return result;
  }),
  complexDenominator: computed('r1', 'r2', 'c1', 'c2', 'sImaginary', 'sSquared', function () {
    var sImaginary = this.sImaginary;
    var sSquared = this.sSquared;

    var r1 = this.r1;
    var r2 = this.r2;
    var c1 = this.c1;
    var c2 = this.c2;

    var denominatorRealTerm = sSquared * r1 * c1 * c2 * r2 + 1;
    var denominatorImaginaryTerm = (r1 * c1 + r1 * c2 + r2 * c2) * sImaginary;
    var result = Complex.createComplex(denominatorRealTerm, denominatorImaginaryTerm);
    return result;
  }),
  complexAlphaPlus: computed('r1', 'r2', 'c1', 'c2', 'complexDenominator', 'sSquared', function () {
    var complexDenominator = this.complexDenominator;
    var sSquared = this.sSquared;

    var r1 = this.r1;
    var r2 = this.r2;
    var c1 = this.c1;
    var c2 = this.c2;

    var result = Complex.createComplex(sSquared * r1 * c1 * c2 * r2, 0).divide(complexDenominator);
    return result;
  }),
  complexBetaPlus: computed('r2', 'c2', 'complexS', 'complexDenominator', function () {
    var complexS = this.complexS;
    var complexDenominator = this.complexDenominator;

    var r2 = this.r2;
    var c2 = this.c2;

    var complexR2 = Complex.createComplex(r2, 0);
    var complexC2 = Complex.createComplex(c2, 0);

    var result = complexR2.mult(complexC2.mult(complexS)).divide(complexDenominator);
    return result;
  }),
  complexRParallel: computed(
    'c1',
    'c2',
    'complexDenominator',
    'complexS',
    'opAmp.rOut',
    'r1',
    'r2',
    'r3',
    'r4',
    'sImaginary',
    'sSquared',
    function () {
      var complexS = this.complexS;
      var complexOne = Complex.createComplex(1, 0);
      var rOut = this.opAmp.rOut;
      var r1 = this.r1;
      var r2 = this.r2;
      var r3 = this.r3;
      var r4 = this.r4;
      var c1 = this.c1;
      var c2 = this.c2;

      var complexC2 = Complex.createComplex(c2, 0);
      var complexC1 = Complex.createComplex(c1, 0);
      var complexR1 = Complex.createComplex(r1, 0);
      var complexR2 = Complex.createComplex(r2, 0);

      var oneByC2S = complexOne.divide(complexC2.mult(complexS));
      var zlookbackpart1 = complexOne.divide(oneByC2S.plus(complexR2)).plus(complexS.mult(complexC1));
      var zlookbackpart1pow = complexOne.divide(zlookbackpart1);
      var complexZlookback = zlookbackpart1pow.plus(complexR1);

      var rParallelReal = 1 / CommonUtils.parallel(rOut, r3 + r4);
      var result = complexOne.divide(Complex.createComplex(rParallelReal, 0).plus(complexOne.divide(complexZlookback))); //complexZlookback overwritten
      return result;
    },
  ),
  complexFF: computed('r1', 'complexRParallel', function () {
    var complexRParallel = this.complexRParallel;
    var r1 = this.r1;
    var complexR1 = Complex.createComplex(r1, 0);
    var result = complexRParallel.divide(complexR1);
    return result;
  }),
  complexAlphaMinus: Complex.createComplex(0, 0),
  complexBetaMinus: computed('r3', 'r4', 'sImaginary', function () {
    var sImaginary = this.sImaginary;
    var r3 = this.r3;
    var r4 = this.r4;

    var complexR3 = Complex.createComplex(r3, 0);
    var complexBetaDenom = Complex.createComplex(
      r3 + r4,
      r4 * r3 * FilterConstants.Limits.PARASITIC_INPUT_CAP * sImaginary,
    );
    var result = complexR3.divide(complexBetaDenom);
    return result;
  }),
  inputImpedance: computed('r1', 'r2', 'c1', 'c2', 'sImaginary', 'complexDenominator', 'complexAmplitude', function () {
    var r1 = this.r1;
    var r2 = this.r2;
    var c1 = this.c1;
    var c2 = this.c2;
    var sImaginary = this.sImaginary;
    var complexDenominator = this.complexDenominator;
    var complexAmplitude = this.complexAmplitude;

    var complexC1s = Complex.createComplex(0, 0);
    complexC1s.imaginary = c1 * sImaginary;

    var term1 = Complex.createComplex(1, r2 * c2 * sImaginary + r1 * c2 * sImaginary);
    var term2 = Complex.createComplex(-1, -1 * r2 * c2 * sImaginary);
    term2 = complexAmplitude.mult(term2);
    var bottom = complexC1s.mult(term1.plus(term2));
    var result = complexDenominator.divide(bottom);
    return result;
  }),
});
